import React from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle } from 'lucide-react';

interface CertificationsSectionProps {
  providerInfo: any;
  form?: any;
  setForm?: React.Dispatch<React.SetStateAction<any>>;
  saving?: boolean;
  readonly?: boolean;
}

export const CertificationsSection = ({
  providerInfo,
  form,
  setForm,
  saving = false,
  readonly = false,
}: CertificationsSectionProps) => {
  const handleSwitchChange = (fieldName: string, checked: boolean) => {
    if (setForm) {
      setForm((prev: any) => ({ ...prev, [fieldName]: checked }));
    }
  };

  const certifications = [
    { key: 'is_esac_accredited', label: 'ESAC Accredited', description: 'Employer Services Assurance Corporation accreditation' },
    { key: 'irs_certified', label: 'IRS Certified', description: 'Internal Revenue Service certified PEO' },
    { key: 'audited', label: 'Audited', description: 'Regularly audited financial statements' },
    { key: 'napeo_member', label: 'NAPEO Member', description: 'National Association of Professional Employer Organizations member' },
    { key: 'bbb_accredited', label: 'BBB Accredited', description: 'Better Business Bureau accreditation' },
    { key: 'aicpa_soc', label: 'AICPA SOC', description: 'AICPA Service Organization Control report' },
    { key: 'fully_bonded_insured', label: 'Fully Bonded & Insured', description: 'Comprehensive bonding and insurance coverage' },
  ];

  if (readonly) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Certifications & Accreditations</h3>
        <div className="grid gap-4">
          {certifications.map((cert) => {
            const isActive = !!providerInfo[cert.key];
            return (
              <div key={cert.key} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  {isActive ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-gray-300" />
                  )}
                  <div>
                    <Label className="text-sm font-medium">{cert.label}</Label>
                    <p className="text-xs text-muted-foreground">{cert.description}</p>
                  </div>
                </div>
                <Badge variant={isActive ? "default" : "secondary"}>
                  {isActive ? "Verified" : "Not Verified"}
                </Badge>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Certifications & Accreditations</h3>
      <p className="text-sm text-muted-foreground">
        These certifications help establish credibility and trust with potential clients.
      </p>
      <div className="grid gap-4">
        {certifications.map((cert) => (
          <div key={cert.key} className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label className="text-sm font-medium">{cert.label}</Label>
              <p className="text-xs text-muted-foreground">{cert.description}</p>
            </div>
            <Switch 
              checked={!!form?.[cert.key]}
              onCheckedChange={(checked) => handleSwitchChange(cert.key, checked)}
              disabled={saving}
            />
          </div>
        ))}
      </div>
    </div>
  );
};